import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TransportDetailStateType } from 'src/types/transportDetail';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import { formatData } from './functions';

const initialState: TransportDetailStateType = {
    data: null,
    changelog: null,
    packageTypeCodes: [],
    apiCalls: null,
    events: null,
    loading: false,
    orderShipmentLoading: false,
    error: false,
};

export const transportDetailSlice = createSlice({
    name: 'transportDetail',
    initialState,
    reducers: {
        getTransportDetailFetch: (state, _action) => {
            state.loading = true;
            state.error = false;
        },
        getTransportDetailFetchSuccess: (state, action) => {
            state.data = formatData(action.payload.data);
            state.changelog = null;
            state.loading = false;
        },
        getTransportDetailFetchFailure: (state, _action) => {
            state.data = null;
            state.changelog = null;
            state.loading = false;
            state.error = true;
        },
        getTransportDetailChangelogFetch: (state, _action) => {
            state.loading = true;
            state.error = false;
        },
        getTransportDetailChangelogFetchSuccess: (state, action) => {
            state.changelog = action.payload.data;
            state.loading = false;
        },
        getTransportDetailChangelogFetchFailure: (state, _action) => {
            state.changelog = null;
            state.loading = false;
            state.error = true;
        },
        createTransportDetailFetch: (state, _action) => {
            state.loading = true;
            state.error = false;
        },
        createTransportDetailFetchSuccess: (state, action) => {
            state.data = formatData(action.payload.data);
            state.loading = false;
            state.changelog = null;
        },
        createTransportDetailFetchFailure: (state, _action) => {
            state.loading = false;
            state.error = true;
            state.changelog = null;
        },
        updateTransportDetailFetch: (state, _action) => {
            state.loading = true;
            state.error = false;
        },
        updateTransportDetailFetchSuccess: (state, action) => {
            state.data = formatData(action.payload.data);
            state.loading = false;
            state.changelog = null;
        },
        updateTransportDetailFetchFailure: (state, _action) => {
            state.loading = false;
            state.error = true;
            state.changelog = null;
        },
        getTransportPackageTypesFetch: (state, _action: PayloadAction<string | undefined>) => {
            // keep previous options while loading
        },
        getTransportPackageTypesFetchSuccess: (state, action: PayloadAction<ConstantsCodeType[]>) => {
            state.packageTypeCodes = action.payload;
        },
        getTransportPackageTypesFetchFailure: (state) => {
            state.packageTypeCodes = [];
        },
        orderTransportShipmentFetch: (state, _action: PayloadAction<{ id: string; force?: boolean }>) => {
            state.orderShipmentLoading = true;
            state.error = false;
        },
        orderTransportShipmentFetchSuccess: (state, action) => {
            state.orderShipmentLoading = false;
            state.apiCalls = null;
            if (action.payload?.data) {
                state.data = formatData(action.payload.data);
            }
        },
        orderTransportShipmentFetchFailure: (state, _action) => {
            state.orderShipmentLoading = false;
            state.error = true;
            state.apiCalls = null;
        },
        getTransportApiCallsFetch: (state, _action: PayloadAction<string>) => {
            // keep previous while loading
        },
        getTransportApiCallsFetchSuccess: (state, action: PayloadAction<TransportDetailStateType['apiCalls']>) => {
            state.apiCalls = action.payload;
        },
        getTransportApiCallsFetchFailure: (state) => {
            state.apiCalls = [];
        },
        getTransportEventsFetch: (state, _action: PayloadAction<string>) => {
            // keep previous while loading
        },
        getTransportEventsFetchSuccess: (state, action: PayloadAction<TransportDetailStateType['events']>) => {
            state.events = action.payload;
        },
        getTransportEventsFetchFailure: (state) => {
            state.events = [];
        },
        getTransportPickupDateFetch: (
            _state,
            _action: PayloadAction<{
                shipper: string;
                hint?: string | null;
                callback: (pickupDate: string | null) => void;
            }>
        ) => {
            // fire-and-forget prefetch for form prefill
        },
        setTransportDetailData: (state, action) => {
            state.data = action.payload;
        },
        clearTransportDetail: () => initialState,
    },
});

export const {
    getTransportDetailFetch,
    getTransportDetailFetchSuccess,
    getTransportDetailFetchFailure,
    getTransportDetailChangelogFetch,
    getTransportDetailChangelogFetchSuccess,
    getTransportDetailChangelogFetchFailure,
    createTransportDetailFetch,
    createTransportDetailFetchSuccess,
    createTransportDetailFetchFailure,
    updateTransportDetailFetch,
    updateTransportDetailFetchSuccess,
    updateTransportDetailFetchFailure,
    getTransportPackageTypesFetch,
    getTransportPackageTypesFetchSuccess,
    getTransportPackageTypesFetchFailure,
    orderTransportShipmentFetch,
    orderTransportShipmentFetchSuccess,
    orderTransportShipmentFetchFailure,
    getTransportApiCallsFetch,
    getTransportApiCallsFetchSuccess,
    getTransportApiCallsFetchFailure,
    getTransportEventsFetch,
    getTransportEventsFetchSuccess,
    getTransportEventsFetchFailure,
    getTransportPickupDateFetch,
    setTransportDetailData,
    clearTransportDetail,
} = transportDetailSlice.actions;

export default transportDetailSlice.reducer;
